<template>
  <AuthLayout>
    <template #form>
      <LoadingScreen v-if="loading" color="var(--purple)" />

      <div v-else class="sso-login-form">
        <div class="sso-login-content">
          <!-- Logo and Title Section -->
          <AuthFormHeader variant="section" title="Log in with SSO" subtitle="Log in with your SSO provider" />

          <!-- Form -->
          <div class="auth-form">
            <div>
              <an-input
                label="Work email address"
                type="email"
                v-model="email"
                :invalid="!emailValid"
                placeholder="name@company.com"
                :theme="theme"
                size="lg"
                focus
                @input="showButton = true"
                @keyup.enter="submit"
              />
              <div v-if="!emailValid && emailTouched" class="auth-field-error" data-cy="sso-email-error">
                {{ errorMessage }}
              </div>
            </div>

            <an-button @click="submit" variant="purple" size="lg" :isWorking="isWorking" :rounded="false">
              Continue
            </an-button>

            <an-link @click="navigateToLogin" class="auth-link-secondary"> Return to log in page </an-link>
          </div>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import { isValidEmail, normalizeEmail } from '@/utils/email';
import api from '@/api';
import { toastError } from '@/services/bus';
import { SignupMixin } from '@/mixins';
import { mapActions } from 'vuex';

export default {
  data() {
    return {
      email: '',
      emailValid: true,
      emailTouched: false,
      errorMessage: '',
      showButton: false,
      isWorking: false,
      theme: 'dark',
      loading: false
    };
  },
  mixins: [SignupMixin],
  components: {
    AuthLayout,
    AuthFormHeader,
    LoadingScreen
  },
  mounted() {
    if (this.$route.query.email) {
      this.email = this.$route.query.email;
      this.showButton = true;
    }

    if (this.$route.query.state) {
      this.loading = true;
      this.completeLogin();
    }

    this.applyTheme(this.theme, 'onboarding');
  },
  methods: {
    ...mapActions({
      getUser: 'users/fetchOne'
    }),
    async completeLogin() {
      try {
        const { data } = await api.post('/sso/auth', { state: this.$route.query.state });
        const { is_login, access_token } = data;
        localStorage.setItem('token', access_token);

        const user = await this.getUser({ id: 'me', skipCache: true });

        this.$tracking.setUserEmail(user.email);
        this.$tracking.setUserId(user.id);
        this.$tracking.alias(user.id);

        if (!is_login) {
          this.$trackEvent('signup.signup.success', {
            cta: 'sso',
            device_id: this.currentDevice.device_id,
            email: user.email
          });
          this.signupExperiments();
        } else {
          this.$trackEvent('login.login.success', {
            cta: 'sso',
            device_id: this.currentDevice.device_id,
            email: user.email
          });
        }

        this.redirectAfterAuth(is_login);
      } catch (err) {
        const { response } = err;
        const message = response?.data?.message || 'Something went wrong! Please try again';
        toastError(message);
        this.$trackEvent('login-sso.complete-login.failure', { error: message });
        this.$router.replace({ name: 'login' });
      }
    },
    async submit() {
      try {
        this.isWorking = true;
        const email = normalizeEmail(this.email);
        this.emailValid = isValidEmail(email);
        this.emailTouched = true;

        this.$trackEvent('login-sso.submit.click', { email });

        if (this.emailValid) {
          this.errorMessage = '';
          const queryParams = new URLSearchParams(this.$route.query);
          const { data } = await api.post('/sso/login', { email, query: queryParams.toString() });
          this.$trackEvent('login-sso.idp-login-url.success', { email });
          window.location.href = data.url;
        } else {
          this.errorMessage = 'Please enter a valid email address';
        }
      } catch (err) {
        const { response } = err;
        const message = response?.data?.message || 'Something went wrong! Please try again with a valid email';
        this.errorMessage = message;
        this.emailValid = false;
        this.emailTouched = true;
        this.$trackEvent('login-sso.idp-login-url.failure', { email: this.email, error: message });
      } finally {
        this.isWorking = false;
      }
    },
    navigateToLogin() {
      this.$trackEvent('login-sso.return-to-login.click');
      this.$router.replace({ name: 'login', query: this.$route.query });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.sso-login-form {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sso-login-content {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-lg;
  padding: 0 $auth-padding-horizontal;
}

.auth-field-error {
  margin-top: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--red);
}

// All shared styles moved to _auth.scss
</style>
